import { z } from 'zod';

/** Required string that rejects whitespace-only values */
const requiredString = z.string().trim().min(1);

const optionalString = z
  .string()
  .optional()
  .transform((v) => v?.trim() || undefined);
const checkbox = z
  .literal('on')
  .optional()
  .transform((v) => v === 'on' || undefined);

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// Nur echte Kalendertage: "2026-02-30" parst zwar, kommt aber als anderer Tag zurueck.
const isValidIsoDate = (value: string) => {
  if (!ISO_DATE.test(value)) return false;
  const time = new Date(`${value}T00:00:00Z`).getTime();

  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === value;
};

const today = () => new Date().toISOString().split('T')[0];

// Zod sammelt alle Refinements; die Zukunftspruefung gilt deshalb nur fuer gueltige Daten,
// damit ein unlesbares Datum nur einen Fehler ergibt.
const notInFuture = (value: string | undefined) => !value || !isValidIsoDate(value) || value <= today();

const optionalDate = z
  .string()
  .optional()
  .transform((v) => v?.trim() || undefined)
  .refine((v) => !v || isValidIsoDate(v), { message: 'invalid-date' });

// Leer meldet schon min(1); ohne das !v kaeme "invalid-date" als zweiter Fehler dazu.
const requiredDate = requiredString.refine((v) => !v || isValidIsoDate(v), { message: 'invalid-date' });

/** Transforms empty string to undefined for select fields */
const optionalEnum = <T extends [string, ...string[]]>(values: T) =>
  z
    .enum(values)
    .or(z.literal(''))
    .optional()
    .nullable()
    .transform((v) => (v === '' ? undefined : v) as (typeof values)[number] | undefined);

export const applicationSchema = z.object({
  salutation: z.enum(['mr', 'ms', 'none']),
  title: optionalString,
  firstName: requiredString,
  lastName: requiredString,
  email: requiredString.email(),
  phone: optionalString,
  street: optionalString,
  streetNumber: optionalString,
  additional: optionalString,
  countryCode: optionalString,
  zipCode: optionalString,
  city: optionalString,
  birthday: optionalDate.refine(notInFuture, { message: 'birthday-future' }),
  birthplace: optionalString,
  fieldOfStudy: optionalString,
  lastAnnualSalary: optionalString,
  lastAnnualSalaryCurrency: optionalString,
  desiredSalary: z.coerce.number().optional(),
  earliestPossibleJoinDate: optionalDate,
  hasNoticePeriod: checkbox,
  noticePeriodTimeAmount: z.coerce.number().optional(),
  noticePeriodTimeUnit: optionalEnum(['days', 'weeks', 'months']),
  noticePeriodTimeReference: optionalEnum([
    'endOfMonth',
    'endOfQuarter',
    'endOfYear',
    'fifteenthOfMonth',
    'fifteenthOrEndOfMonth',
    'onEveryDay',
  ]),
  isSeverelyDisabled: checkbox,
  howDidYouHearAboutUs: z.string().trim().default(''),
  remark: optionalString,
  permissionToStoreApplication: checkbox,
  mayBePassedWithinCorporateGroup: checkbox,
  // hasPrivacyTermsAccepted hat kein Formularfeld – wird in submitApplication fest auf true gesetzt.
});

/** Maps visibleFields keys to form field names that should be required when Mandatory */
const mandatoryFieldMap: Record<string, string[]> = {
  mobilePhone: ['phone'],
  address: ['street', 'streetNumber', 'zipCode', 'city', 'countryCode'],
  birthday: ['birthday'],
  birthPlace: ['birthplace'],
  fieldOfStudy: ['fieldOfStudy'],
  lastAnnualSalary: ['lastAnnualSalary'],
  desiredSalary: ['desiredSalary'],
  earliestPossibleJoinDate: ['earliestPossibleJoinDate'],
  noticePeriod: ['noticePeriodTimeAmount', 'noticePeriodTimeUnit', 'noticePeriodTimeReference'],
  remark: ['remark'],
};

/** Builds a dynamic schema that makes fields required based on visibleFields config */
export const buildApplicationSchema = (visibleFields?: Record<string, string> | null) => {
  if (!visibleFields) return applicationSchema;

  const overrides: Record<string, z.ZodTypeAny> = {};
  const noticePeriodVisible =
    visibleFields.noticePeriod &&
    visibleFields.noticePeriod !== 'NotVisible' &&
    visibleFields.noticePeriod !== 'Administrator';

  for (const [settingsKey, formKeys] of Object.entries(mandatoryFieldMap)) {
    if (visibleFields[settingsKey] === 'Mandatory') {
      for (const key of formKeys) {
        // noticePeriod sub-fields handled conditionally below
        if (settingsKey === 'noticePeriod') continue;
        if (key === 'birthday') {
          overrides[key] = requiredDate.refine(notInFuture, { message: 'birthday-future' });
          continue;
        }
        if (key === 'earliestPossibleJoinDate') {
          overrides[key] = requiredDate;
          continue;
        }
        overrides[key] = key === 'desiredSalary' ? z.coerce.number().min(1) : requiredString;
      }
    }
  }

  const baseSchema = Object.keys(overrides).length > 0 ? applicationSchema.extend(overrides) : applicationSchema;

  // Conditional validation: noticePeriod fields required when checkbox is checked
  // This applies whenever the notice period section is visible (user can opt-in)
  if (noticePeriodVisible) {
    return baseSchema.superRefine((data, ctx) => {
      if (data.hasNoticePeriod !== true) return;

      if (!data.noticePeriodTimeAmount || data.noticePeriodTimeAmount <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'number',
          inclusive: true,
          path: ['noticePeriodTimeAmount'],
        });
      }
      if (!data.noticePeriodTimeUnit) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          expected: 'string',
          received: 'undefined',
          path: ['noticePeriodTimeUnit'],
        });
      }
      if (!data.noticePeriodTimeReference) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          expected: 'string',
          received: 'undefined',
          path: ['noticePeriodTimeReference'],
        });
      }
      // Max 2 years validation
      const maxByUnit: Record<string, number> = { days: 730, weeks: 104, months: 24 };
      const max = data.noticePeriodTimeUnit ? maxByUnit[data.noticePeriodTimeUnit] || 730 : 730;
      if (data.noticePeriodTimeAmount && data.noticePeriodTimeAmount > max) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_big,
          maximum: max,
          type: 'number',
          inclusive: true,
          path: ['noticePeriodTimeAmount'],
          message: 'notice-period-max',
        });
      }
    });
  }

  return baseSchema;
};

export type ApplicationFormData = z.infer<typeof applicationSchema>;
